import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * The four UI primitives the ported canvas uses.
 *
 * Shadowland Title's ui.jsx has twelve; carrying the other eight would be
 * dead code, and its own icon set alongside them would be a second icon
 * system in an app whose UI audit exists to keep one. The three glyphs
 * those primitives reached for come from this app's lucide shim instead.
 */

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="empty">
      <span className="spinner" style={{ marginRight: 9, verticalAlign: '-3px' }} />
      {label}
    </div>
  );
}

export function Notice({ kind = 'info', children, onDismiss }) {
  if (!children) return null;
  return (
    <div className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div className="spread">
        <span>{children}</span>
        {onDismiss && (
          <button type="button" className="btn-ghost btn-sm" onClick={onDismiss} aria-label="Dismiss">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="field">
      {label && <span>{label}</span>}
      {children}
      {hint && <div className="field-hint">{hint}</div>}
    </label>
  );
}

export function Modal({ title, subtitle, onClose, children, footer, wide = false }) {
  const dialog = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  /**
   * The dialog TAKES FOCUS, and gives it back on the way out.
   *
   * Without this a modal is only a picture over the page: the keyboard
   * stays behind it, so tabbing walks the page underneath and every
   * shortcut registered there still fires. Opening a deed over a chain
   * of title and pressing an arrow key to turn the page was nudging the
   * box behind the modal and saving the move — a document somebody
   * opened to READ was quietly editing the chart.
   *
   * Focus goes to whatever the dialog offers first, or to the dialog
   * itself; on close it returns to whatever opened it, so the page does
   * not throw a keyboard user back to the top.
   */
  useEffect(() => {
    const came = document.activeElement;
    const box = dialog.current;
    const first = box?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first || box)?.focus?.({ preventScroll: true });
    return () => came?.focus?.({ preventScroll: true });
  }, []);

  // Rendered into <body> rather than in place. `position: fixed` resolves
  // against the viewport only while no ancestor establishes a containing
  // block — and backdrop-filter does exactly that, which .card uses. A modal
  // opened from inside a card was therefore being clipped to that card
  // instead of covering the screen. A portal makes the modal independent of
  // wherever it happens to be opened from.
  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className={`modal ${wide ? 'modal-wide' : ''}`}
        role="dialog" aria-modal="true" aria-label={title}
        ref={dialog} tabIndex={-1}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <div className="faint" style={{ marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
